/**
 * The choreography of **both** motions of the questions panel in *one* place:
 * the morph main chat → panel (`AssistantMorphOverlay`) and the ordinary
 * open/close via the „Fragen" button (`AppShell`, `SiteHeader`). Times in
 * milliseconds, curves, nothing else. Pure — no DOM, no React, no motion
 * import. Everything that can be decided about the sequence is therefore
 * testable in jsdom (`morphTimeline.test.ts`); the geometry itself is not (jsdom
 * does not measure rectangles).
 *
 * ## One motion language, two occasions
 *
 * Product owner's requirement of 22.09.2026: „richtig clean und smooth
 * animiert, ohne dass es zuviel Schatten ist" (really clean and smooth, without
 * too much shadow) — and right after that, for the button: „mach beim Klick auf
 * ‚Fragen' noch eine passende Open/Close Animation" (add a matching open/close
 * animation when clicking „Fragen"). *Matching* here means: the same curve
 * family, the same ordering of durations, no second vocabulary. That is why the
 * panel constants (`PANEL_*`) live in the same file as the morph constants and
 * not in a file of their own.
 *
 * ## Why a curve and no longer a spring
 *
 * Until 22.09.2026 the shell flew on a time-bound spring with `bounce: 0.12`.
 * That is exactly the overshoot the product owner criticised in the finished
 * morph: the edge runs past the target rectangle and comes back — for a surface
 * that docks against a window edge, that looks like wobbling, not like calm.
 *
 * `EASE_MORPH_FLIGHT` is the curve Apple uses for its sheets:
 * `cubic-bezier(0.32, 0.72, 0, 1)`. It starts briskly, reaches its highest
 * velocity early and covers the last percent of the way over a long, flat
 * tail — the impression of a mass arriving, **without** a single frame going
 * past the target. One value, one direction, no ringing: exactly one arc of
 * motion.
 *
 * `SPRING_LAYOUT` (`src/lib/ease.ts`) is still **not touched** — `SharedLayoutBg`,
 * `animated-sidebar`, `preview-rail`, `Tree`, `message-bubble` and the agent
 * activity all depend on it. A change there would be a change to all of them.
 */

/**
 * Curves in exactly the shape motion accepts as `Easing` (`motion-utils`): a
 * `readonly` 4-tuple (`BezierDefinition`) or one of the keywords. No `number[]`
 * and no free `string` — both fall outside `Easing`, and the keyframe tracks
 * here return whole curve *lists* (`Easing[]`).
 */
export type MorphEase =
    | readonly [number, number, number, number]
    | 'linear'
    | 'easeIn'
    | 'easeOut'
    | 'easeInOut';

/**
 * **The** curve of the flight and of all tracks that run with it: the Apple
 * sheet curve. See the file header — no overshoot, one arc. It also lives in
 * `src/lib/ease.ts` as `EASE_DRAWER`; it is repeated here because
 * `morphTimeline.ts` deliberately stays import-free (the test runs without the
 * motion token file, and the morph must not change when someone sharpens a
 * curve there).
 */
export const EASE_MORPH_FLIGHT: MorphEase = [0.32, 0.72, 0, 1];

/** Soft arrival (decelerating) — the curve of opening/closing. */
export const EASE_MORPH_OUT: MorphEase = [0.22, 1, 0.36, 1];

/** Clean exit (accelerating) — for everything that fades out. */
export const EASE_MORPH_IN: MorphEase = [0.4, 0, 0.2, 1];

/**
 * The flight of the shell from the chat rectangle to the panel rectangle.
 * 500 ms: long enough for the eye to follow the path across half the window
 * width, short enough that the click does not turn into a show. The requirement
 * is 480–520 ms.
 */
export const MORPH_FLIGHT_MS = 500;

/** Handover: crossfade shell → real panel. Two nearly identical images. */
export const MORPH_HANDOVER_MS = 120;

/**
 * Total duration in the normal case — the number the „≤ 640 ms" requirement is
 * measured against. The cap `MORPH_PANEL_READY_CAP_MS` can stretch it when the
 * panel's history fetch hangs.
 */
export const MORPH_TOTAL_MS = MORPH_FLIGHT_MS + MORPH_HANDOVER_MS;

/**
 * The handover happens here at the latest, even if the real panel does not have
 * its history yet (`markPanelReady` never arrives). It then fades in with help
 * text instead of history — visible, but capped.
 *
 * The cap is **the** number that applies in the failure case, and is therefore
 * tied to the requirement: `MORPH_PANEL_READY_CAP_MS + MORPH_HANDOVER_MS` must
 * stay within 640 ms, otherwise the failure case of all things takes longest.
 * 520 + 120 = 640 — the upper bound, not the usual case (500 + 120 = 620).
 */
export const MORPH_PANEL_READY_CAP_MS = 640 - MORPH_HANDOVER_MS;

/**
 * Hard upper bound. The only **guaranteed** way back to "no morph": animation
 * callbacks never fire in jsdom, nor when an animation is aborted. As long as
 * this watchdog runs, the panel can never get stuck invisible. It is
 * deliberately generous — it is a safety net, not a timing value.
 */
export const MORPH_MAX_MS = 1200;

/**
 * Deadline within which the overlay must measure the target rectangle of the
 * `aside` (two `requestAnimationFrame`s plus `ResizeObserver`). After that:
 * abort, the panel appears abruptly. Kept short — it is the path into the
 * failure case, not the normal case.
 */
export const MORPH_TARGET_MEASURE_MS = 250;

/** An abort fades the shell out instead of cutting it off (otherwise it flashes). */
export const MORPH_CANCEL_FADE_MS = 100;

/**
 * `prefers-reduced-motion`: no flight, no shadow, only opacity. The same number
 * applies to the button and to the panel when motion is turned off.
 */
export const MORPH_REDUCED_CROSSFADE_MS = 120;

/** The panel appears without motion of its own (handover from the main chat). */
export const MORPH_PLAIN_FADE_MS = 180;

/**
 * Radius of the flying shell halfway through; start and target are flush (0).
 * 12 px is the upper limit of the requirement: the surface reads as a card
 * without the rounding becoming a motif of its own. More radius needs more
 * shadow to avoid looking like it floats — and shadow is exactly what was
 * supposed to go.
 */
export const MORPH_CARD_RADIUS_PX = 12;

/**
 * The **only** shadow in the whole morph: soft, set low, barely visible.
 * Requirement: `0 8px 24px rgba(0,0,0,0.06–0.08)`. It separates the flying
 * surface from the background instead of lifting it off; it fades in with the
 * flight and out again before docking, so the docked panel sits flush (the real
 * `aside` has no shadow).
 */
export const MORPH_SHADOW = '0 8px 24px rgb(0 0 0 / 0.07)';

/**
 * The transition of the flight (`x/y/width/height`): one duration, one curve.
 * No spring, no `bounce` — see the file header.
 */
export const MORPH_FLIGHT_TRANSITION = {
    duration: MORPH_FLIGHT_MS / 1000,
    ease: EASE_MORPH_FLIGHT,
} as const;

/** A track: from `startMs` to `endMs` (absolute, from the start of the flight). */
export interface MorphSpan {
    startMs: number;
    endMs: number;
    ease: MorphEase;
}

/**
 * **One** track for both content layers: layer A (the clone of the chat
 * surface) fades out while layer B (the panel preview) fades in — simultaneously,
 * same length, same curve. No staggering, no offset.
 *
 * 160 ms in the middle of the flight (170 → 330 of 500). There used to be two
 * tracks with different times and curves; that produced a phase in which both
 * images stood half-visible, and that is exactly where the impression of "too
 * much going on" comes from. A symmetric crossfade around the middle is the
 * calmest solution: at the start you see the chat, at the end the panel, and in
 * between both for a brief moment.
 */
export const SPAN_CROSSFADE: MorphSpan = { startMs: 170, endMs: 330, ease: EASE_MORPH_FLIGHT };

/**
 * The shadow rises with the start and is gone again before docking. Kept short
 * (160 ms up, 160 ms down): it should accompany the flight, not have a motion of
 * its own.
 */
export const SPAN_SHADOW_IN: MorphSpan = { startMs: 0, endMs: 160, ease: EASE_MORPH_FLIGHT };
export const SPAN_SHADOW_OUT: MorphSpan = { startMs: 340, endMs: 500, ease: EASE_MORPH_FLIGHT };

/**
 * The panel's hairline (`border-l` of the `aside`) is a separate overlay that
 * does **not fly along**: a 1 px border on a box that changes its width by a
 * factor of ~3 frays. It only appears for docking — the last 80 ms of the
 * flight — and is in place the moment the handover begins.
 *
 * The same rule applies to the shadow (its own layer, `opacity` only) — and as
 * a guardrail for future changes: **no `border` or `box-shadow` on any element
 * that undergoes a non-uniform size or transform change.**
 */
export const SPAN_HAIRLINE_IN: MorphSpan = { startMs: 420, endMs: 500, ease: EASE_MORPH_FLIGHT };

/* ------------------------------------------------------------------ *
 * Opening and closing the panel via the „Fragen" button (A13)
 * ------------------------------------------------------------------ */

/**
 * The curve of opening and closing. Deliberately `EASE_MORPH_OUT` and not the
 * flight curve: the morph carries a surface **across** the screen and therefore
 * needs the long tail; the panel only slides in at its edge. Both curves are
 * decelerating and without overshoot — the same language, two distances.
 */
export const PANEL_EASE: MorphEase = EASE_MORPH_OUT;

/**
 * The panel slides in: 300 ms. The page content next to it moves over in the
 * same time (the `aside` animates its **width**, see `AppShell`), and a layout
 * change of this magnitude needs more time than a plain fade-in, otherwise it
 * feels like a jump. Below the flight (500 ms) because the distance is shorter.
 */
export const PANEL_OPEN_MS = 300;

/** Going out is a little faster than coming in — disappearing must not wait. */
export const PANEL_CLOSE_MS = 240;

/**
 * The content (transcript + input line) follows the edge instead of starting
 * with it: 180 ms from 80 ms, i.e. done at 260 ms — just before the edge comes
 * to rest. The delay has two reasons. First, it reads as *one* process: first
 * the space opens, then there is something in it. Second, the content is
 * invisible for the first 80 ms and still faint afterwards — exactly during the
 * time the width change is re-wrapping it.
 */
export const PANEL_CONTENT_DELAY_MS = 80;
export const PANEL_CONTENT_IN_MS = 180;

/** When closing, the content goes first — in the first half of the tail. */
export const PANEL_CONTENT_OUT_MS = 120;

/** The distance the content travels while doing so. 6 px: a hint, not a flight. */
export const PANEL_CONTENT_SHIFT_PX = 6;

/**
 * The state change in the button (icon rotates and swaps, border/fill change).
 * 200 ms — fast enough to belong to the click, slow enough that you see the
 * change.
 */
export const PANEL_ICON_SWAP_MS = 200;

/** The button's press point. 0.96 is the requirement: noticeable, not rubbery. */
export const PANEL_PRESS_SCALE = 0.96;

/** Milliseconds → motion seconds, rounded to 3 decimal places. */
export function toSeconds(ms: number): number {
    return Math.round(ms) / 1000;
}

/**
 * A track as a motion `transition` (delay + duration in seconds).
 * For layers that fade in **one** direction only.
 */
export function spanTransition(span: MorphSpan): {
    delay: number;
    duration: number;
    ease: MorphEase;
} {
    return {
        delay: toSeconds(span.startMs),
        duration: toSeconds(span.endMs - span.startMs),
        ease: span.ease,
    };
}

/**
 * Two tracks (in + out) on **one** element as keyframes: values `[0, 1, 1, 0]`
 * at the four points in time, normalised to the total duration. Today only used
 * for the shadow — the veil over the target page was dropped without
 * replacement on 22.09.2026 (requirement: no scrim, no blur).
 */
export function pulseKeyframes(
    rise: MorphSpan,
    fall: MorphSpan,
): { values: number[]; times: number[]; duration: number; ease: MorphEase[] } {
    const total = fall.endMs;
    const times = [rise.startMs, rise.endMs, fall.startMs, fall.endMs].map((ms) => ms / total);
    return {
        values: [0, 1, 1, 0],
        times,
        duration: toSeconds(total),
        ease: [rise.ease, 'linear', fall.ease],
    };
}

/**
 * The shell's radius over the flight: flush → card → flush. It runs with the
 * shadow (0 → 120 ms up, 340 → 500 ms back), so rounding and shadow are one
 * motif and not two. It ends at 0 — the real `aside` is flush.
 */
export function radiusKeyframes(): {
    values: number[];
    times: number[];
    duration: number;
    ease: MorphEase[];
} {
    const total = MORPH_FLIGHT_MS;
    return {
        values: [0, MORPH_CARD_RADIUS_PX, MORPH_CARD_RADIUS_PX, 0],
        times: [0, 120 / total, 340 / total, 1],
        duration: toSeconds(total),
        ease: [EASE_MORPH_FLIGHT, 'linear', EASE_MORPH_FLIGHT],
    };
}

/**
 * When the handover begins: after the flight, but not before the real panel has
 * its history — capped, so that a hanging network fetch does not drag the morph
 * out.
 */
export function handoverStartMs(panelReadyAtMs: number | null): number {
    if (panelReadyAtMs === null) return MORPH_PANEL_READY_CAP_MS;
    return Math.min(Math.max(MORPH_FLIGHT_MS, panelReadyAtMs), MORPH_PANEL_READY_CAP_MS);
}
